import { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { readBusBooking, saveBusBooking } from "./Busbooking";
import "./Busbooking.css";
import { blockTicket } from "../../../../services/busApi";

// ── Helper ────────────────────────────────────────────────────────────────
const splitCity = (value) => String(value || "").split(",")[0] || "-";
const extractPointName = (point) => {
  if (!point) return "";
  if (typeof point === "string") return point;
  return point.cityPointName ?? point.name ?? point.location ?? point.address ?? "";
};

// ── Payload builder (exported for use in BusSeatModal) ───────────────────
export function createBusBookingPayload({
  bus, from, to, date,
  selectedSeats, boarding, dropping, repriceData, searchTrackId,
}) {
  const baseFare =
    repriceData?.fare ??
    repriceData?.baseFare ??
    selectedSeats.reduce((sum, s) => sum + (s.price || 0), 0);

  const gst = repriceData?.gst ?? Math.round(baseFare * 0.05);
  const discount = repriceData?.discount ?? 0;
  const grandTotal =
    repriceData?.totalFare ??
    repriceData?.grandTotal ??
    baseFare + gst - discount;

  return {
    // IDs needed for block-ticket API call
    busId: bus.id ?? bus.busId,
    searchTrackId: searchTrackId ?? repriceData?.searchTrackId ?? null,
    boardingPointId: boarding?.bpId ?? boarding?.id ?? boarding?.boardingPointId ?? "",
    droppingPointId: dropping?.bpId ?? dropping?.id ?? dropping?.droppingPointId ?? "",

    bus: {
      operator: bus.operator,
      busType: bus.busType,
      departure: bus.departure,
      arrival: bus.arrival,
      duration: bus.duration,
      seatType: bus.seatType,
      isAC: bus.isAC,
    },
    route: {
      from, to, date,
      boarding: boarding?.cityPointName ?? boarding?.name ?? boarding?.location ?? "",
      dropping: dropping?.cityPointName ?? dropping?.name ?? dropping?.location ?? "",
    },
    seats: selectedSeats.map((s) => ({
  code: s.code,
  price: s.price,
  label: s.seatType || "Seater",
  seatType: s.seatType || "Seater",
})),
    // number of passengers derived from seats — no manual field needed
    passengerCount: selectedSeats.length,
    price: {
      baseFare,
      onwardFare: baseFare,
      gst,
      discount,
      grandTotal,
    },
    travellers: [],  // filled on the booking page
    contact: {},  // filled on the booking page
  };
}

// ── Validation helpers ────────────────────────────────────────────────────
// ── Validation helpers ────────────────────────────────────────────────────
const TITLE_GENDER = { Mr: "MALE", Mrs: "FEMALE", Ms: "FEMALE" };

const NAME_REGEX = /^[A-Za-z][A-Za-z\s'-]{1,49}$/;
const GSTIN_REGEX = /^\d{2}[A-Z]{5}\d{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;

function validateTravellerField(traveller, key) {
  switch (key) {
    case "title":
      if (!traveller.title) return "Select a title";
      return "";
    case "firstName":
      if (!traveller.firstName.trim()) return "First name is required";
      if (!NAME_REGEX.test(traveller.firstName.trim())) return "Letters only, 2–50 chars";
      return "";
    case "lastName":
      if (!traveller.lastName.trim()) return "Last name is required";
      if (!NAME_REGEX.test(traveller.lastName.trim())) return "Letters only, 2–50 chars";
      return "";
    case "age": {
      const n = Number(traveller.age);
      if (!traveller.age || isNaN(n)) return "Enter a valid age";
      if (!Number.isInteger(n)) return "Age must be a whole number";
      if (n < 1 || n > 120) return "Age must be between 1 and 120";
      return "";
    }
    default:
      return "";
  }
}

function validateAllTravellers(travellers) {
  const errors = {};
  let firstMessage = null;
  travellers.forEach((t, i) => {
    ["title", "firstName", "lastName", "age"].forEach((key) => {
      const msg = validateTravellerField(t, key);
      if (msg) {
        errors[`traveller-${i}-${key}`] = msg;
        if (!firstMessage) firstMessage = `Passenger ${i + 1}: ${msg}`;
      }
    });
  });
  return { errors, firstMessage };
}

function validateContactField(contact, key) {
  switch (key) {
    case "email":
      if (!contact.email.trim()) return "Email is required";
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact.email.trim())) return "Enter a valid email address";
      return "";
    case "phone": {
      const digits = contact.phone.trim().replace(/\D/g, "");
      if (!digits) return "Phone number is required";
      if (digits.length !== 10) return "Enter a valid 10-digit phone number";
      if (!/^[6-9]/.test(digits)) return "Enter a valid Indian mobile number";
      return "";
    }
    case "gstNo": {
      const val = (contact.gstNo || "").trim();
      if (!val) return ""; // optional field
      if (!GSTIN_REGEX.test(val.toUpperCase())) return "Enter a valid 15-character GSTIN";
      return "";
    }
    default:
      return "";
  }
}

function validateAllContact(contact) {
  const errors = {};
  let firstMessage = null;
  ["email", "phone", "gstNo"].forEach((key) => {
    const msg = validateContactField(contact, key);
    if (msg) {
      errors[`contact-${key}`] = msg;
      if (!firstMessage) firstMessage = msg;
    }
  });
  return { errors, firstMessage };
}

// ── Component ─────────────────────────────────────────────────────────────
export default function BusBooking() {
  const location = useLocation();
  const navigate = useNavigate();
  const [fieldErrors, setFieldErrors] = useState({});
  const [openTitleDropdown, setOpenTitleDropdown] = useState(null);
  // booking comes from navigation state or localStorage fallback
  const booking = location.state?.booking ?? readBusBooking();
  // FIX: initialise travellers from booking.seats — no reference before init
  const initialTravellers = useMemo(() => {
    const seats = booking?.seats ?? [];
    return seats.map((seat, idx) => ({
      label: idx === 0 ? "Adult" : `Adult ${idx + 1}`,
      seat,
      title: "",
      firstName: "",
      lastName: "",
      age: "",
    }));
  }, [booking]);

  // All state declared at the top — no hooks after early return
  const [travellers, setTravellers] = useState(initialTravellers);
  const [contact, setContact] = useState({ email: "", phone: "", gstNo: "" });
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");

  // ── Early return AFTER all hooks ─────────────────────────────────────────
  if (!booking) {
    return (
      <main className="bb-page bb-empty">
        <h1>No bus booking selected</h1>
        <p>Please select seats from the buses page to continue booking.</p>
        <button type="button" onClick={() => navigate("/buses")}>Go to Buses</button>
      </main>
    );
  }

  const { bus, route, seats, passengerCount, price } = booking;

  const adminPricing = booking.adminPricing ?? {};
const supplierFare = Number(
  booking.supplierFareTotal ?? adminPricing.supplierFareTotal ?? price.grandTotal ?? 0
);
const payableTotal = Number(
  booking.payableTotal ?? booking.adminPayableTotal ?? adminPricing.payableTotal
);
const hasAdminPrice = Number.isFinite(payableTotal) && payableTotal >= 0;
const money = (value) =>
  `₹${Number(value ?? 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

  // passengerCount is always derived from seats — no hardcoding
  const numPassengers = passengerCount ?? seats?.length ?? 0;

  const seatLabel = seats
  .map(
    (s) =>
      `${s.code} (${s.seatType || s.label || bus.seatType || "General"})`
  )
  .join(", ");

  const updateTraveller = (index, key, value) => {
    setTravellers((curr) =>
      curr.map((t, i) => (i === index ? { ...t, [key]: value } : t))
    );
    setFieldErrors((prev) => {
      const next = { ...prev };
      delete next[`traveller-${index}-${key}`];
      return next;
    });
  };

  const updateContact = (key, value) => {
    setContact((c) => ({ ...c, [key]: value }));
    setFieldErrors((prev) => {
      const next = { ...prev };
      delete next[`contact-${key}`];
      return next;
    });
  };

  // ── Continue / block ticket ───────────────────────────────────────────────
  const handleContinueBooking = async () => {
    setFormError("");

    const { errors: travellerErrors, firstMessage: travellerMsg } = validateAllTravellers(travellers);
    const { errors: contactErrors, firstMessage: contactMsg } = validateAllContact(contact);
    const allErrors = { ...travellerErrors, ...contactErrors };

    setFieldErrors(allErrors);

    if (Object.keys(allErrors).length > 0) {
      setFormError(travellerMsg || contactMsg);
      return;
    }

    if (!agreed) { setFormError("Please agree to the Terms & Conditions."); return; }

    // ── Read IDs from actual booking shape ────────────────────────────────
    const busId = booking.busId ?? booking.bus?.id;
    const bpId = String(booking.boardingPointId ?? booking.route?.boarding?.bpId ?? "");
    const dpId = String(booking.droppingPointId ?? booking.route?.dropping?.bpId ?? "");
    const searchTrackId = booking.searchTrackId ?? booking.route?.searchTrackId ?? booking._searchTrackId;


    if (!busId) { setFormError("Bus ID missing — please go back and select a bus again."); return; }
    if (!bpId || bpId === "0") { setFormError("Boarding point ID missing."); return; }
    if (!dpId || dpId === "0") { setFormError("Dropping point ID missing."); return; }
    if (!searchTrackId) { setFormError("Search session expired — please search again."); return; }

    // Map UI titles (Mr/Mrs/Ms) to the API's expected uppercase enum (MR/MRS/MS)
    const TITLE_API = { Mr: "MR", Mrs: "MRS", Ms: "MS" };

    const passengerDetails = travellers.map((p) => ({
      title: TITLE_API[p.title] ?? "MR",
      firstName: p.firstName.trim(),
      lastName: p.lastName.trim(),
      gender: TITLE_GENDER[p.title] ?? "MALE",
      age: Number(p.age),
      seatNo: p.seat.seatId ?? p.seat.code,
    }));

    const requestId = `REQ-${Date.now()}-${Math.random().toString(36).slice(2, 8).toUpperCase()}`;

    // ── DEBUG: how stale is this session? ──
    const searchAgeMs = booking.searchedAt ? Date.now() - booking.searchedAt : null;
    console.log("[BLOCK DEBUG] Time since search:", searchAgeMs ? `${Math.round(searchAgeMs / 1000)}s` : "unknown (no searchedAt on booking)");
    console.log("[BLOCK DEBUG] busId:", busId, "bpId:", bpId, "dpId:", dpId, "searchTrackId:", searchTrackId);
    console.log("[BLOCK DEBUG] seat codes being sent:", seats.map(s => s.code));

    setSubmitting(true);
    try {
      console.log("[BLOCK] requestId:", requestId);
      console.log("[BLOCK] passengerDetails being sent:", JSON.stringify(passengerDetails, null, 2));
      console.log("[BLOCK] busId:", busId, "bpId:", bpId, "dpId:", dpId, "searchTrackId:", searchTrackId);
      const blockResult = await blockTicket({
  requestId,
  busId,
  bpId,
  dpId,
  searchTrackId,
  passengerDetails,
  contactDetails: {
    email: contact.email.trim(),
    phoneNo: contact.phone.trim().replace(/\s/g, ""),
    dialCode: "91",
  },
  gstNo: contact.gstNo?.trim() ?? "",
});

      console.log("[BLOCK PAYLOAD SENT]", JSON.stringify({
        requestId,
        busId,
        bpId,
        dpId,
        searchTrackId,
        passengerDetails,
        contactDetails: {
          email: contact.email.trim(),
          phoneNo: contact.phone.trim(),
          dialCode: "91",
        },
      }, null, 2));
      console.log("[BLOCK] resolved:", blockResult);
      console.log("[BLOCK] resolved FULL:", JSON.stringify(blockResult, null, 2));
      console.log("[BLOCK] extracted blockId:", blockResult.blockKey);
      const blockId = blockResult?.blockKey;  // ← declare it here from blockResult

      if (!blockId) {
        setFormError("Seat block failed — no block ID returned. Please try again.");
        setSubmitting(false);
        return;
      }
      const confirmedBooking = {
        ...booking,
        blockId,                                              // ✅ guaranteed defined
        busId: booking.busId,                        // ✅ keep original — blockResult has no busId
        searchTrackId: booking.searchTrackId,                // ✅ keep original
        travellers: travellers.map((t, i) => ({
          ...t,
          name: `${t.firstName.trim()} ${t.lastName.trim()}`.trim() || `Passenger ${i + 1}`,
        })),
        contact: {
          email: contact.email.trim(),
          phone: contact.phone.trim(),
        },
      };

      console.log("[BLOCK] confirmedBooking.blockId:", confirmedBooking.blockId); // verify

      saveBusBooking(confirmedBooking);
      navigate("/busconfirm", { state: { booking: confirmedBooking } });
    } catch (err) {
      console.error("Block ticket error:", err);
      setFormError(err?.message ?? "Could not block ticket. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <main className="bb-page">
      <section className="bb-main">

        {/* ── Booking Details ── */}
        <div className="bb-card">
          <div className="bb-card-head">Booking Details</div>
          <div className="bb-booking-grid">

            <div className="bb-route-title">
              <div className="bb-bus-icon">▣</div>
              <div>
                <strong>{splitCity(route.from)} → {splitCity(route.to)}</strong>
                <span>
                  {route.date?.label ?? route.date ?? "—"},{" "}
                  {route.date?.day ?? ""} 2026
                </span>
              </div>
            </div>

            <div className="bb-time-box">
              <span>Departure Time</span>
              <strong>{bus.departure}</strong>
            </div>

            <div className="bb-duration-mini">
              <i /><span>🚌</span><i />
              <em>{bus.duration}</em>
            </div>

            <div className="bb-time-box">
              <span>Arrival Time</span>
              <strong>{bus.arrival}</strong>
            </div>

            <div className="bb-time-box">
              <span>Seat no(s)</span>
              <mark>{seatLabel}</mark>
            </div>

            <div className="bb-time-box">
              <span>No of Passenger(s)</span>
              {/* FIX: use numPassengers derived from seats, not a stale variable */}
              <strong>{numPassengers}</strong>
            </div>

            <div className="bb-address-block">
              <div className="bb-stop-line"><i /><i /></div>
              <div>
                <span>Boarding Time &amp; Address</span>
                <strong>{bus.departure} {extractPointName(route.boarding)}</strong>
              </div>
              <div>
                <span>Dropping Time &amp; Address</span>
                <strong>{bus.arrival} {extractPointName(route.dropping)}</strong>
              </div>
            </div>

            <div className="bb-operator-box">
              <span>Bus Operator</span>
              <strong>{bus.operator}</strong>
              <em>{bus.busType}</em>
            </div>
          </div>
        </div>

        {/* ── Traveller Details ── */}
<div className="bb-card bb-traveller-card-section">
          <div className="bb-card-head bb-head-split">
            <span>Travellers Details</span>
            <small>▣ Name should match Government ID proof</small>
          </div>
          <div className="bb-travellers">
            <div className="bb-traveller-header">
              <span />
              <span>Title</span>
              <span>First Name</span>
              <span>Last Name</span>
              <span>Age</span>
              <span>Seat</span>
            </div>
            {travellers.map((row, index) => (
              <div
  className={`bb-traveller-row ${
    openTitleDropdown === index ? "dropdown-open" : ""
  }`}
  key={row.seat.code}
>
                <strong>{row.label}</strong>

                <div className="bb-field" data-label="Title">
  <div className="bb-title-picker">

    <button
      type="button"
      className={`bb-title-trigger ${
        fieldErrors[`traveller-${index}-title`]
          ? "bb-input-error"
          : ""
      }`}
      onClick={() =>
        setOpenTitleDropdown((current) =>
          current === index ? null : index
        )
      }
    >
      <span>{row.title || "Title"}</span>
      <span className="bb-title-chevron">⌄</span>
    </button>

    {openTitleDropdown === index && (
      <div className="bb-title-menu">
        {["Mr", "Mrs", "Ms"].map((title) => (
          <button
            key={title}
            type="button"
            className={`bb-title-option ${
              row.title === title ? "selected" : ""
            }`}
            onClick={() => {
              updateTraveller(index, "title", title);
              setOpenTitleDropdown(null);
            }}
          >
            {title}
          </button>
        ))}
      </div>
    )}

  </div>

  {fieldErrors[`traveller-${index}-title`] && (
    <small className="bb-field-error">
      {fieldErrors[`traveller-${index}-title`]}
    </small>
  )}
</div>

                <div className="bb-field" data-label="First Name">
                  <input
                    placeholder="First Name"
                    value={row.firstName}
                    className={fieldErrors[`traveller-${index}-firstName`] ? "bb-input-error" : ""}
                    onChange={(e) => updateTraveller(index, "firstName", e.target.value)}
                  />
                  {fieldErrors[`traveller-${index}-firstName`] && (
                    <small className="bb-field-error">{fieldErrors[`traveller-${index}-firstName`]}</small>
                  )}
                </div>

                <div className="bb-field" data-label="Last Name">
                  <input
                    placeholder="Last Name"
                    value={row.lastName}
                    className={fieldErrors[`traveller-${index}-lastName`] ? "bb-input-error" : ""}
                    onChange={(e) => updateTraveller(index, "lastName", e.target.value)}
                  />
                  {fieldErrors[`traveller-${index}-lastName`] && (
                    <small className="bb-field-error">{fieldErrors[`traveller-${index}-lastName`]}</small>
                  )}
                </div>

                <div className="bb-field" data-label="Age">
                  <input
                    placeholder="Age"
                    type="number"
                    min="1"
                    max="120"
                    value={row.age}
                    className={fieldErrors[`traveller-${index}-age`] ? "bb-input-error" : ""}
                    onChange={(e) => updateTraveller(index, "age", e.target.value)}
                  />
                  {fieldErrors[`traveller-${index}-age`] && (
                    <small className="bb-field-error">{fieldErrors[`traveller-${index}-age`]}</small>
                  )}
                </div>

                <div
  className="bb-seat-pill"
  data-label="Seat"
>
  {row.seat.code} ({row.seat.seatType || row.seat.label || "General"})
</div>
              </div>
            ))}
          </div>
        </div>

        {/* ── Contact Details ── */}
        <div className="bb-card bb-contact-card">
          <h3>Contact Details</h3>
          <p>Your ticket &amp; bus details will be shared here</p>
          <div className="bb-contact-grid">
            <label>
              Email Address
              <input
                type="email"
                placeholder="Enter your Email ID"
                value={contact.email}
                className={fieldErrors["contact-email"] ? "bb-input-error" : ""}
                onChange={(e) => updateContact("email", e.target.value)}
              />
              {fieldErrors["contact-email"] && (
                <small className="bb-field-error">{fieldErrors["contact-email"]}</small>
              )}
            </label>
            <label>
              Phone Number
              <div className="bb-phone-input">
                <span>+91</span>
                <input
                  type="tel"
                  placeholder="10-digit mobile number"
                  maxLength={10}
                  value={contact.phone}
                  className={fieldErrors["contact-phone"] ? "bb-input-error" : ""}
                  onChange={(e) => updateContact("phone", e.target.value.replace(/\D/g, ""))}
                />
              </div>
              {fieldErrors["contact-phone"] && (
                <small className="bb-field-error">{fieldErrors["contact-phone"]}</small>
              )}
            </label>
          </div>
        </div>

        {/* ── GSTIN (optional) ── */}
        <div className="bb-gstin-block">
          <details>
            <summary className="bb-gstin">✚ Use GSTIN for this booking <span>(OPTIONAL)</span></summary>
            <div style={{ padding: "10px 0 4px" }}>
              <input
                className={`bb-gstin-input ${fieldErrors["contact-gstNo"] ? "bb-input-error" : ""}`}
                placeholder="Enter GSTIN number"
                value={contact.gstNo}
                onChange={(e) => updateContact("gstNo", e.target.value.toUpperCase())}
              />
              {fieldErrors["contact-gstNo"] && (
                <small className="bb-field-error">{fieldErrors["contact-gstNo"]}</small>
              )}
            </div>
          </details>
        </div>

        {/* ── Terms ── */}
        <label className="bb-terms">
          <input
            type="checkbox"
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
          />
          I agree with{" "}
          <a href="/terms">T&amp;C</a>,{" "}
          <a href="/bus-policies">Bus Policies</a> and{" "}
          <a href="/privacy">Privacy Policy</a>
        </label>

        {/* ── Error banner ── */}
        {formError && (
          <div className="bb-error-banner" role="alert">
            ⚠️ {formError}
          </div>
        )}

        {/* ── CTA ── */}
        <button
          type="button"
          className="bb-continue"
          disabled={!agreed || submitting}
          onClick={handleContinueBooking}
        >
          {submitting ? "Processing…" : "Continue Booking →"}
        </button>
      </section>

      {/* ── Price Summary ── */}
<aside className="bb-price-card">
  <div className="bb-card-head">Price Summary</div>

  <div className="bb-price-row">
    <span>Supplier Fare Total</span>
    <strong>{money(supplierFare)}</strong>
  </div>

  {Array.isArray(adminPricing.breakdown) &&
    adminPricing.breakdown.map((item, index) => (
      <div className="bb-price-row" key={item.id ?? item.code ?? index}>
        <span>{item.label ?? item.name ?? item.description ?? "Price adjustment"}</span>
        <strong>{money(item.amount ?? item.total ?? item.value ?? 0)}</strong>
      </div>
    ))}

  <div className="bb-price-row total">
    <span>Final Payable Amount</span>
    <strong>
      {hasAdminPrice ? money(payableTotal) : "Calculating…"}
    </strong>
  </div>

  {!hasAdminPrice && (
    <div role="alert" style={{ color: "#b91c1c", fontSize: 12, marginTop: 8 }}>
      Admin pricing is unavailable. Please go back and select the bus again.
    </div>
  )}
</aside>
    </main>
  );
}